import { MqttCombination, MqttTimestamp } from '@/shared/DefaultValues'
import { getOptions, timezoneItems } from '@/shared/SelectData'
import { TemplateTransferConfigProps } from '@/template/interface'
import { Guid } from '@/utils/guid'
import { getFormatMessage } from '@/utils/utils'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs
} from 'antd'
import React, { useState } from 'react'
const { TabPane } = Tabs

export const TemplateTransferConfig: React.FC<TemplateTransferConfigProps> = (
  props: TemplateTransferConfigProps
) => {
  const [visible, setVisible] = useState<boolean>(false)
  const [tabKey, setTabKey] = useState<string>('combination')
  const [combination, setCombination] = useState<MqttCombination>({})
  const [timestamp, setTimestamp] = useState<MqttTimestamp>({})
  const [form] = Form.useForm()
  const formatMessage = getFormatMessage()
  const combeCols = [
    {
      title: formatMessage({
        id: 'app.template.transfer.config.combination.name'
      }),
      key: 'app.template.transfer.config.combination.name',
      dataIndex: 'name'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.config.combination.format'
      }),
      key: 'app.template.transfer.config.combination.format',
      dataIndex: 'format'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.config.combination.description'
      }),
      key: 'app.template.transfer.config.combination.description',
      dataIndex: 'description'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.config.combination.operation'
      }),
      key: 'app.template.transfer.config.combination.operation',
      render: (text, record) => (
        <Space size='middle'>
          <a
            onClick={() => {
              handleCombinationEdit(record)
            }}
          >
            {formatMessage({
              id: 'app.template.transfer.config.combination.operation.edit'
            })}
          </a>
          <a
            onClick={() => {
              handleCombinationDelete(record)
            }}
          >
            {formatMessage({
              id: 'app.template.transfer.config.combination.operation.delete'
            })}
          </a>
        </Space>
      )
    }
  ]

  const timestampCols = [
    {
      title: formatMessage({
        id: 'app.template.transfer.config.timestamp.name'
      }),
      key: 'app.template.transfer.config.timestamp.name',
      dataIndex: 'name'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.config.timestamp.format'
      }),
      key: 'app.template.transfer.config.timestamp.format',
      dataIndex: 'format'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.config.timestamp.zone'
      }),
      key: 'app.template.transfer.config.timestamp.zone',
      dataIndex: 'zone'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.config.timestamp.description'
      }),
      key: 'app.template.transfer.config.timestamp.description',
      dataIndex: 'description'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.config.timestamp.operation'
      }),
      key: 'app.template.transfer.config.timestamp.operation',
      render: (text, record) => (
        <Space size='middle'>
          <a
            onClick={() => {
              handleTimestampEdit(record)
            }}
          >
            {formatMessage({
              id: 'app.template.transfer.config.timestamp.operation.edit'
            })}
          </a>
          <a
            onClick={() => {
              handleTimestampDelete(record)
            }}
          >
            {formatMessage({
              id: 'app.template.transfer.config.timestamp.operation.delete'
            })}
          </a>
        </Space>
      )
    }
  ]

  const timestampDescCols = [
    {
      title: '格式',
      key: '1',
      dataIndex: 'format',
      width: 150
    },
    {
      title: '描述',
      key: '1',
      dataIndex: 'description'
    }
  ]

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 }
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 }
    }
  }

  const labelCol = {
    xs: { span: 12 },
    sm: { span: 3 }
  }

  const wrapperCol = {
    xs: { span: 24 },
    sm: { span: 20 }
  }

  const timestampDescData = [
    {
      id: Guid.raw(),
      format: 'y',
      description: 'yyyy:四位数年(1970),yy:2位数年(70) [组合使用]'
    },
    {
      id: Guid.raw(),
      format: 'MM',
      description: '月份 [组合使用]'
    },
    {
      id: Guid.raw(),
      format: 'dd',
      description: '日 [组合使用]'
    },
    {
      id: Guid.raw(),
      format: 'hh',
      description: '时 [组合使用]'
    },
    {
      id: Guid.raw(),
      format: 'mm',
      description: '分 [组合使用]'
    },
    {
      id: Guid.raw(),
      format: 'ss',
      description: '秒 [组合使用]'
    },
    {
      id: Guid.raw(),
      format: 'ttt',
      description: '毫秒 [组合使用]'
    },
    {
      id: Guid.raw(),
      format: 'unix-long-m-utc',
      description: 'linux时间戳(utc),long类型,毫秒级'
    },
    {
      id: Guid.raw(),
      format: 'unix-long-s-utc',
      description: 'linux时间戳(utc),long类型,秒级'
    },
    {
      id: Guid.raw(),
      format: 'unix-string-m-utc',
      description: 'linux时间戳(utc),string类型,毫秒级utc'
    },
    {
      id: Guid.raw(),
      format: 'unix-string-s-utc',
      description: 'linux时间戳(utc),string类型 秒级utc'
    },
    {
      id: Guid.raw(),
      format: 'unix-float-m-utc',
      description: 'Linux时间戳(utc),float类型 毫秒级'
    }
  ]

  const submitCombination = () => {
    form
      .validateFields()
      .then((value) => {
        const merged = Object.assign(combination, value)
        if (!merged.id) merged.id = Guid.raw()
        console.log(merged)
        if (props.onCombinationCreate) {
          props.onCombinationCreate(merged)
        }
        form.resetFields()
        setCombination({})
        setVisible(false)
      })
      .catch((info) => {
        console.log('Validate Failed:', info)
      })
  }

  const submitTimestamp = () => {
    form
      .validateFields()
      .then((value) => {
        const merged = Object.assign(timestamp, value)
        if (!merged.id) merged.id = Guid.raw()
        if (props.onTimestampCreate) {
          props.onTimestampCreate(merged)
        }

        form.resetFields()
        setTimestamp({})
        setVisible(false)
      })
      .catch((info) => {
        console.log('Validate Failed:', info)
      })
  }

  const handleOnCreate = () => {
    setVisible(true)
    setCombination({})
    setTimestamp({})
    form.resetFields()
    if (tabKey == 'timestamp') {
      form.setFieldsValue({ zone: 'utc' })
    }
  }

  const handleCombinationEdit = (item: MqttCombination) => {
    if (item.isDefault) {
      Modal.error({
        title: formatMessage({ id: 'app.modal.confirm.edit.forbidden' }),
        content: '系统默认组合方式不可编辑',
        okText: formatMessage({ id: 'app.modal.ok' })
      })
      return
    }
    setCombination(item)
    form.setFieldsValue(item)
    setVisible(true)
  }

  const handleCombinationDelete = (item: MqttCombination) => {
    if (item.isDefault) {
      Modal.error({
        title: formatMessage({ id: 'app.modal.confirm.delete.forbidden' }),
        content: '系统默认组合方式不可删除',
        okText: formatMessage({ id: 'app.modal.ok' })
      })
    } else {
      Modal.confirm({
        title: formatMessage({ id: 'app.modal.confirm.delete' }),
        icon: <ExclamationCircleOutlined />,
        okText: formatMessage({ id: 'app.modal.ok' }),
        cancelText: formatMessage({ id: 'app.modal.cancel' }),
        content: formatMessage({
          id: 'app.template.transfer.config.combination.delete.message'
        }),
        onOk() {
          if (props.onCombinationDelete) {
            props.onCombinationDelete(item)
          }
        }
      })
    }
  }

  const handleTimestampEdit = (item: MqttTimestamp) => {
    if (item.isDefault) {
      Modal.error({
        title: formatMessage({ id: 'app.modal.confirm.edit.forbidden' }),
        content: '系统默认时间格式不可编辑',
        okText: formatMessage({ id: 'app.modal.ok' })
      })
      return
    }
    setTimestamp(item)
    form.setFieldsValue(item)
    setVisible(true)
  }

  const handleTimestampDelete = (item: MqttTimestamp) => {
    if (item.isDefault) {
      Modal.error({
        title: formatMessage({ id: 'app.modal.confirm.delete.forbidden' }),
        content: '系统默认时间格式不可删除',
        okText: formatMessage({ id: 'app.modal.ok' })
      })
    } else {
      Modal.confirm({
        title: formatMessage({ id: 'app.modal.confirm.delete' }),
        icon: <ExclamationCircleOutlined />,
        okText: formatMessage({ id: 'app.modal.ok' }),
        cancelText: formatMessage({ id: 'app.modal.cancel' }),
        content: formatMessage({
          id: 'app.template.transfer.config.combination.delete.message'
        }),
        onOk() {
          if (props.onTimestampDelete) {
            props.onTimestampDelete(item)
          }
        }
      })
    }
  }

  const handleCombinationBlur = (value: string) => {
    if (!value || value == '') return
    const regex = /^[\],:{}\s]*$/
    if (
      regex.test(
        value
          .replace(/\\["\\\/bfnrtu]/g, '@')
          .replace(
            /"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?/g,
            ']'
          )
          .replace(/(?:^|:|,)(?:\s*\[)+/g, '')
      )
    ) {
      const txt = value
        .replace('%name%', '温度')
        .replace('%value%', '25.6')
        .replace('%timestamp%', '2020-07-26 14:08:59')
        .replace('%quality%', '1')
        .replace('%unit%', '℃')
        .replace('%id%', '000001')
      const item = form.getFieldsValue()
      item.preview = txt
      form.setFieldsValue(item)
    } else {
      message.error('JSON格式错误')
    }
  }

  const handleTimestampBlur = (value: string) => {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth() + 1
    const day = now.getDate()
    const hours = now.getHours()
    const minutes = now.getMinutes()
    const seconds = now.getSeconds()
    const milliseconds = now.getMilliseconds()
    const ms = now.getTime()
    const str = value
      .replace('unix-long-m-utc', `${ms}`)
      .replace('unix-long-s-utc', `${ms / 1000}`)
      .replace('unix-string-m-utc', ms.toString())
      .replace('unix-string-s-utc', (ms / 1000).toString())
      .replace('unix-float-m-utc', ms.toString() + '.0')
      .replace('yyyy', `${year}`)
      .replace('yyy', `${year}`)
      .replace('yy', `${year.toString().substring(2)}`)
      .replace('y', `${year.toString().substring(2)}`)
      .replace('MM', `${month.toString().padStart(2, '0')}`)
      .replace('M', `${month.toString().padStart(2, '0')}`)
      .replace('dd', `${day.toString().padStart(2, '0')}`)
      .replace('d', `${day.toString().padStart(2, '0')}`)
      .replace('hh', `${hours.toString().padStart(2, '0')}`)
      .replace('mm', `${minutes.toString().padStart(2, '0')}`)
      .replace('m', `${minutes.toString().padStart(2, '0')}`)
      .replace('ss', `${seconds.toString().padStart(2, '0')}`)
      .replace('s', `${seconds.toString().padStart(2, '0')}`)
      .replace('ttt', `${milliseconds.toString().padStart(3, '0')}`)
      .replace('tt', `${milliseconds.toString().padStart(3, '0')}`)
      .replace('t', `${milliseconds.toString().padStart(3, '0')}`)
    const item = form.getFieldsValue()
    item.preview = str
    form.setFieldsValue(item)
  }

  const handleModalOk = () => {
    if (tabKey == 'combination') {
      submitCombination()
    } else {
      submitTimestamp()
    }
  }

  const getTitle = () => {
    if (tabKey === 'combination') {
      return combination.id
        ? formatMessage({
            id: 'app.template.transfer.config.combination.title.edit',
            parameters: { name: combination.name }
          })
        : formatMessage({
            id: `app.template.transfer.config.combination.title`
          })
    } else {
      return timestamp.id
        ? formatMessage({
            id: 'app.template.transfer.config.timestamp.title.edit',
            parameters: { name: timestamp.name }
          })
        : formatMessage({
            id: `app.template.transfer.config.timestamp.title`
          })
    }
  }

  return (
    <div>
      <Tabs
        defaultActiveKey={tabKey}
        onChange={setTabKey}
        tabBarExtraContent={
          <Button onClick={handleOnCreate}>
            {formatMessage({ id: 'app.template.transfer.config.create' })}
          </Button>
        }
      >
        <TabPane
          tab={formatMessage({
            id: 'app.template.transfer.config.combination'
          })}
          key='combination'
        >
          <Table
            rowKey='id'
            columns={combeCols}
            dataSource={props.combinations}
          ></Table>
        </TabPane>
        <TabPane
          tab={formatMessage({
            id: 'app.template.transfer.config.timestamp'
          })}
          key='timestamp'
        >
          <Table
            rowKey='id'
            columns={timestampCols}
            dataSource={props.timestamps}
          ></Table>
        </TabPane>
      </Tabs>

      <Modal
        maskClosable={false}
        destroyOnClose={true}
        title={getTitle()}
        visible={visible}
        okText={formatMessage({ id: 'app.modal.ok' })}
        cancelText={formatMessage({ id: 'app.modal.cancel' })}
        onOk={handleModalOk}
        onCancel={() => setVisible(false)}
        width={700}
      >
        {tabKey == 'combination' && (
          <Form form={form} {...formItemLayout}>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.config.combination.name'
                  })}
                  name='name'
                  rules={[
                    {
                      required: true,
                      transform: (value) => value?.trim(),
                      message: formatMessage({
                        id: 'app.template.transfer.config.combination.name.message'
                      })
                    }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.config.combination.description'
                  })}
                  name='description'
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  labelCol={labelCol}
                  wrapperCol={wrapperCol}
                  label={formatMessage({
                    id: 'app.template.transfer.config.combination.format'
                  })}
                  name='format'
                  rules={[
                    {
                      required: true,
                      transform: (value) => value?.trim(),
                      message: formatMessage({
                        id: 'app.template.transfer.config.combination.format.message'
                      })
                    }
                  ]}
                >
                  <Input.TextArea
                    style={{ minHeight: 100 }}
                    onBlur={(e) => handleCombinationBlur(e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  labelCol={labelCol}
                  wrapperCol={wrapperCol}
                  label={formatMessage({
                    id: 'app.template.transfer.config.combination.format.preview'
                  })}
                  name='preview'
                >
                  <Input.TextArea readOnly={true} style={{ minHeight: 100 }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  labelCol={labelCol}
                  wrapperCol={wrapperCol}
                  label={formatMessage({
                    id: 'app.template.transfer.config.combination.description'
                  })}
                >
                  <p>
                    替换符:
                    <br />
                    监控点名称：%name%,值%value%,时间戳:%timestamp%,消息质量:%quality%,单位:%unit%,Id:%id%
                    <br />
                    预览中使用:
                    <br />
                    温度25.6℃,时间戳20200727,消息质量为1,单位位℃,Id为000001
                  </p>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}

        {tabKey == 'timestamp' && (
          <Form form={form} {...formItemLayout}>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.config.timestamp.name'
                  })}
                  name='name'
                  rules={[
                    {
                      required: true,
                      transform: (value) => value?.trim(),
                      message: formatMessage({
                        id: 'app.template.transfer.config.timestamp.name.message'
                      })
                    }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.config.timestamp.description'
                  })}
                  name='description'
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.config.timestamp.format'
                  })}
                  name='format'
                  rules={[
                    {
                      required: true,
                      transform: (value) => value?.trim(),
                      message: formatMessage({
                        id: 'app.template.transfer.config.timestamp.format.message'
                      })
                    }
                  ]}
                >
                  <Input onBlur={(e) => handleTimestampBlur(e.target.value)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.config.timestamp.zone'
                  })}
                  name='zone'
                  rules={[
                    {
                      required: true,
                      message: formatMessage({
                        id: 'app.template.transfer.config.timestamp.zone.message'
                      })
                    }
                  ]}
                >
                  <Select>{getOptions(timezoneItems)}</Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={24}>
                <Table
                  rowKey='id'
                  size='small'
                  columns={timestampDescCols}
                  dataSource={timestampDescData}
                  pagination={{ pageSize: 6 }}
                  bordered
                  style={{ margin: '0px 20px' }}
                />
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  labelCol={labelCol}
                  wrapperCol={wrapperCol}
                  label={formatMessage({
                    id: 'app.template.transfer.config.timestamp.format.preview'
                  })}
                  name='preview'
                >
                  <Input.TextArea readOnly={true} style={{ minHeight: 100 }} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Modal>
    </div>
  )
}
